import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import { useSelector } from '../redux/store';
import { PATH_AUTH, PATH_PAGE } from '../routes/paths';
import { isOnTimeUserLogin } from '../utils/sessionFlags';

const AuthGuard = ({ children }) => {
  const { isAuthenticated, isDevicePaired: isDevicePairedAuth, env, user } = useAuth();
  // SocketContext.isDevicePaired n'est rafraîchi que par INITIALIZE/LOGIN ; après postDevice
  // seul le slice Redux est mis à jour. On le considère comme source complémentaire de vérité
  // pour éviter une redirection vers /pointages/pairing juste après un pairing réussi (CW-21956 bis).
  const { isDevicePaired: isDevicePairedRedux } = useSelector((state) => state.clockings);
  const isDevicePaired = isDevicePairedAuth || isDevicePairedRedux;
  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();

  useEffect(() => {
    // Pas connecté
    if (!isAuthenticated) {
      // Espace de connexion d'un appareil Comète On Time
      if (window.env.isClockingApp) {
        if (!env.isClockingSiteEnabled) {
          return navigate(PATH_AUTH.clockingApp.clockingAppDisabled);
        }

        // Erreur d'authentification (agent/client sans appareil configuré) :
        // forcer le retour login pour afficher le message, ne pas rediriger vers l'init
        if (sessionStorage.getItem('ontime_login_error')) {
          if (pathname !== PATH_AUTH.clockingApp.login) {
            navigate(PATH_AUTH.clockingApp.login);
          }
          return;
        }

        // Si l'appareil n'est pas associé
        if (!isDevicePaired) {
          if (pathname !== PATH_AUTH.clockingApp.clockingInit) {
            navigate(PATH_AUTH.clockingApp.clockingInit);
          }
        } else if (pathname !== PATH_AUTH.clockingApp.login) {
          navigate(PATH_AUTH.clockingApp.login);
        }
        // Espace de connexion classique
      } else if (pathname !== PATH_AUTH.login) {
        // CW-22023 : on ne préserve plus l'URL via state.from. À chaque
        // connexion à un espace, on arrive sur le dashboard.
        navigate(PATH_AUTH.login);
      }
    } else if (window.env.isClockingApp) {
      // Page d'information : Comète On Time n'est pas activée
      if (!env.isClockingSiteEnabled) {
        return navigate(PATH_AUTH.clockingApp.clockingAppDisabled);
      }

      // Login OnTime "réel" : email (manager/agent/client) OU code agent en MCE.
      // Voir `src/utils/sessionFlags.ts` pour les flags pris en compte.
      const isEmailLogin = isOnTimeUserLogin();

      // Utilisateur OnTime déconnecté (flag email/code retiré) mais user encore en state :
      // resource, client OU manager → tous ramenés sur /pointages (ClockingInAndOutPage =
      // code + email). On ne redirige PAS vers /pointages/login, /pointages/init ou
      // /pointages/pairing après un logout : ces pages perdent leur sens UX dans le
      // contexte « l'utilisateur vient de se déconnecter, le prochain doit pouvoir
      // se connecter ici ».
      //
      // Exception : un manager qui se logue sur un appareil non encore appairé doit être
      // routé vers le setup (device-type / pairing), pas vers /pointages — c'est lui qui
      // configure la pointeuse/MCE. resource/client n'ont pas ce droit donc on les laisse
      // sur /pointages même sur device non appairé.
      if (!isEmailLogin && (user?.role === 'resource' || user?.role === 'client' || user?.role === 'manager')) {
        if (user?.role === 'manager' && !isDevicePaired) {
          // Bouncing post-logout : `isDevicePaired` peut être transitoirement false
          // (deux sources async : useAuth().env vs state.clockings). Si on est déjà
          // sur une page kiosque (typiquement /pointages ou ses sous-pages après un
          // logout), on NE redirige PAS vers le setup — c'est un logout intentionnel.
          //
          // Le fresh login manager passe par `GuestGuard` (depuis `/` après que
          // LoginPage navigate vers `from`) qui gère le routage initial vers
          // device-type / pairing. Donc on ne perd pas cette UX ici.
          const kioskAllowedPathsForLogout = [
            PATH_AUTH.clockingApp.clockingInAndOut,
            PATH_AUTH.clockingApp.forgotClockingCode,
            PATH_AUTH.clockingApp.login,
            PATH_PAGE.clockingApp.clockingsShiftsLists,
            PATH_PAGE.clockingApp.clockablesShiftsList,
            PATH_PAGE.clockingApp.shift
          ];
          if (kioskAllowedPathsForLogout.includes(pathname)) {
            return;
          }
          const hasDeviceType = !!localStorage.getItem('comete_device_type');
          if (!hasDeviceType && pathname !== PATH_AUTH.clockingApp.clockingDeviceType) {
            return navigate(PATH_AUTH.clockingApp.clockingDeviceType);
          }
          if (
            hasDeviceType &&
            pathname !== PATH_AUTH.clockingApp.devicePairing &&
            pathname !== PATH_AUTH.clockingApp.settings &&
            pathname !== PATH_AUTH.clockingApp.clockingDeviceType
          ) {
            navigate(PATH_AUTH.clockingApp.devicePairing);
          }
          return;
        }
        const kioskAllowedPaths = [
          PATH_AUTH.clockingApp.clockingInAndOut,
          PATH_AUTH.clockingApp.forgotClockingCode,
          PATH_AUTH.clockingApp.login,
          // Pages secondaires du flow kiosque : Planning site, choix de vacation,
          // vacation en cours. Sans ces autorisations, un resource/client/manager
          // codé sur le kiosque (sans flag email actif) qui clique "Planning site"
          // est immédiatement renvoyé vers /pointages → ping-pong d'URL.
          PATH_PAGE.clockingApp.clockingsShiftsLists,
          PATH_PAGE.clockingApp.clockablesShiftsList,
          PATH_PAGE.clockingApp.shift,
          // Manager : on autorise /pointages/settings pour qu'il puisse voir
          // l'écran "Configuration terminée" juste après le pairing (et l'édition
          // ultérieure des paramètres). resource/client n'iront jamais sur cette
          // page faute de droits côté UI, donc pas de risque côté kiosque.
          ...(user?.role === 'manager' ? [PATH_AUTH.clockingApp.settings] : [])
        ];
        if (!kioskAllowedPaths.includes(pathname)) {
          navigate(PATH_AUTH.clockingApp.clockingInAndOut, { replace: true });
        }
        return;
      }

      // Client : pas besoin de pairing, accès direct
      if (user?.role === 'client') {
        if (!pathname.includes('/pointages') || pathname === '/pointages' || pathname === '/pointages/') {
          navigate(PATH_AUTH.clockingApp.clientHome);
        }
        // Manager connecté par email : accès direct au dashboard manager
      } else if (user?.role === 'manager' && sessionStorage.getItem('ontime_email_login')) {
        if (!pathname.includes('/pointages') || pathname === '/pointages' || pathname === '/pointages/') {
          navigate(PATH_AUTH.clockingApp.managerHome);
        }
        // Agent connecté (email ou code MCE) : accès direct au dashboard agent, ne PAS forcer le pairing.
        // Le pairing concerne le rôle `clocking_device` (la tablette), pas l'agent qui s'est authentifié
        // sur son propre compte — son `isDevicePaired` peut légitimement être `false` en dev/CI.
      } else if (user?.role === 'resource' && isEmailLogin) {
        if (!pathname.includes('/pointages') || pathname === '/pointages' || pathname === '/pointages/') {
          navigate(PATH_AUTH.clockingApp.resourceHome);
        }
        // Appareil Comète On Time non appairé
      } else if (!isDevicePaired) {
        const hasDeviceType = !!localStorage.getItem('comete_device_type');
        // Choix du type d'appareil avant le pairing
        if (!hasDeviceType && pathname !== PATH_AUTH.clockingApp.clockingDeviceType) {
          return navigate(PATH_AUTH.clockingApp.clockingDeviceType);
        }
        if (
          hasDeviceType &&
          pathname !== PATH_AUTH.clockingApp.devicePairing &&
          pathname !== PATH_AUTH.clockingApp.settings &&
          pathname !== PATH_AUTH.clockingApp.clockingDeviceType
        ) {
          navigate(PATH_AUTH.clockingApp.devicePairing);
        }
        // Espace de pointage pour les agents
      } else if (pathname === PATH_AUTH.clockingApp.devicePairing || !pathname.includes('/pointages')) {
        if (user?.role === 'resource') {
          navigate(PATH_AUTH.clockingApp.resourceHome);
        } else if (user?.role === 'client') {
          navigate(PATH_AUTH.clockingApp.clientHome);
        } else {
          navigate(PATH_AUTH.clockingApp.clockingInAndOut);
        }
      } else if (user?.role === 'resource' && (pathname === '/pointages' || pathname === '/pointages/')) {
        navigate(PATH_AUTH.clockingApp.resourceHome);
        // Branche pré-existante détectée comme dead code par ESLint
        // (déjà couverte plus haut). Hors scope CW-22023.
        // eslint-disable-next-line no-dupe-else-if
      } else if (user?.role === 'client' && (pathname === '/pointages' || pathname === '/pointages/')) {
        navigate(PATH_AUTH.clockingApp.clientHome);
      }
    }
    // Dep `navigate` volontairement omise (préexistant, hors scope CW-22023).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, isDevicePaired, isAuthenticated, env, user]);

  return children;
};

AuthGuard.propTypes = {
  children: PropTypes.node
};

export default AuthGuard;
